import { Button, Form, Input, Modal, Switch } from 'antd';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BRAND } from '@/constants/brand';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from '@/modules/categories/categoriesApi';
import type { Category } from '@/types/category';
import { resolveMutationResult } from '@/utils/apiResponse';
import styles from './CategoryFormModal.module.scss';

interface CategoryFormModalProps {
  open: boolean;
  category?: Category | null;
  onClose: () => void;
}

interface CategoryFormValues {
  name: string;
  isActive: boolean;
}

export function CategoryFormModal({ open, category, onClose }: CategoryFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(category);
  const [createCategory, createState] = useCreateCategoryMutation();
  const [updateCategory, updateState] = useUpdateCategoryMutation();
  const isLoading = createState.isLoading || updateState.isLoading;
  useGlobalLoader(isLoading);

  const { control, handleSubmit, reset } = useForm<CategoryFormValues>({
    defaultValues: {
      name: '',
      isActive: true,
    },
  });

  useMutationReset(createState.reset);
  useMutationReset(updateState.reset);

  useEffect(() => {
    if (!open) return;

    reset({
      name: category?.name ?? '',
      isActive: category?.isActive ?? true,
    });
  }, [category, open, reset]);

  const onSubmit = async (values: CategoryFormValues) => {
    const payload = {
      name: values.name.trim(),
      isActive: values.isActive,
    };

    if (isEdit) {
      if (!category?.id) return;

      const result = await resolveMutationResult(updateCategory({ id: category.id, body: payload }));
      if (result.ok) {
        notify.success(result.message ?? 'Category updated successfully.');
        onClose();
        return;
      }

      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createCategory(payload));

    if (result.ok) {
      notify.success(result.message ?? 'Category created successfully.');
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title={isEdit ? 'Edit category' : 'Add category'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={480}
    >
      <Form layout="vertical" className={styles.form} onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Name"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} placeholder="Category name" />
            </Form.Item>
          )}
        />

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Form.Item label="Status" className={styles.statusField}>
              <Switch
                checked={field.value}
                onChange={field.onChange}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                style={{ background: field.value ? BRAND.primary : undefined }}
              />
            </Form.Item>
          )}
        />

        <div className={styles.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Save changes' : 'Create category'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
